import React, { FC, useEffect } from 'react';
import styled from '@emotion/styled';

import { ItemFragmentOnItem_upsales } from '../GridButtons/__queries__';
import CloseButton from '~ComponentsImproved/CloseButton';
import { SelectedUpsales } from './';
import { useI18n } from '~Hooks';
import { useButton } from '~Hooks';
import { FormattedModificationGroup } from '~Hooks/ItemModifications/useItemModifications';
import { Box, GrayButton, GreenButton } from '~Styles';
import Column from './Column';
import Grid from './Grid/index';
import { getSplittedTitle } from './utils';

const Content = styled.div<{ isGridView?: boolean }>`
  display: flex;
  flex-direction: ${({ isGridView }) => (isGridView ? 'column' : 'row')};
  overflow-y: auto;
  overflow-x: ${({ isGridView }) => (isGridView ? 'hidden' : 'auto')};
  height: 100%;
  position: absolute;
  width: 100%;
  height: 100vh;
  padding: 20px 32px 0;
  box-sizing: border-box;
  justify-content: flex-start;
  align-items: flex-start;
`;

const AddButton = styled(GreenButton)`
  height: 80px;
  width: 100%;
`;

const OkButton = styled(GrayButton)`
  height: 80px;
  width: 100%;
  color: #bdbdbd;
  background: #838383;
  box-shadow: 0 0 20px rgb(0 0 0 / 25%);
`;

const Backdrop = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
`;

const ButtonWrapper = styled.div`
  width: 100%;
  position: absolute;
  display: flex;
  flex-flow: column nowrap;
  justify-content: center;
  align-items: center;
  padding: 40px 40px 32px;
  box-sizing: border-box;
  bottom: 0;
  background: linear-gradient(180deg, rgba(47, 53, 61, 0) 10%, #2f353d 100%);

  button {
    margin-bottom: 24px;
    box-shadow: '0px 0px 8px rgba(0, 0, 0, 0.16), 0px 16px 24px rgba(0, 0, 0, 0.16), 0px 0px 32px rgba(0, 0, 0, 0.12)';
  }
`;

export const Tab = styled(Box)<{
  minWidth?: string;
  maxWidth?: string;
}>`
  border-radius: 4px;
  background-color: transparent;
  font-weight: 600;
  letter-spacing: var(--letterSpacing);
  color: var(--colorWhite);
  height: 60px;
  min-height: 60px;
  padding: 4px 16px;
  box-sizing: border-box;
  max-width: 300px;
  pointer-events: auto;
  margin: 0 8px 8px;

  max-width: ${({ maxWidth }) => maxWidth ?? '300px'};
  min-width: ${({ minWidth }) => minWidth ?? 'auto'};

  & > div {
    height: 100%;
    display: flex;
    flex-flow: column nowrap;
    justify-content: center;
  }

  span {
    display: block;
    font-size: 80%;
    margin-bottom: 4px;
    letter-spacing: var(--letterSpacing);
  }
`;

export interface Props {
  modificationGroups: FormattedModificationGroup[];
  upsellGroups: ItemFragmentOnItem_upsales[] | undefined;
  selectedVariations: {
    id: string;
    name: string;
    price: number;
    asItem:
      | {
          id: string;
          categories: {
            id: string;
            name: string;
          }[];
        }
      | undefined;
  }[];
  selectedUpsales: SelectedUpsales;
  isEditing: boolean;
  onSelectVariation: (itemId: string) => void;
  onSelectUpsale: (itemId: string, itemTitle?: string) => void;
  addItemToCart: () => void;
  onClose: () => void;
}

/** The popup that appears when changing modifications for a product */
const ItemModificationView: FC<Props> = ({
  modificationGroups,
  upsellGroups,
  selectedVariations,
  selectedUpsales,
  isEditing,
  onSelectVariation,
  onSelectUpsale,
  addItemToCart,
  onClose,
}) => {
  const { i18n } = useI18n();

  const showGridView = modificationGroups.some((group) => group.isGridViewEnabled);

  // If there is only one modification group and it's type is single AND a modification is selected, add automatically to cart
  const isToCloseInstant =
    modificationGroups.length === 1 &&
    modificationGroups[0].type === 'SINGLE' &&
    !isEditing &&
    !modificationGroups.some((g) => g.preselectedVariations.length > 0);

  // When all variation groups that are marked as required have a selection, the variations selection
  // is considered valid. Until then the add button is locked, so required modifiers can't be skipped.
  const selectionValid = modificationGroups.every((group) => group.valid);

  // Names of required groups still missing a selection, listed in the toast so staff know exactly
  // what to pick (especially in grid view, where group titles are easy to miss). "Step 1, " prefixes are dropped.
  const missingGroupNames = modificationGroups
    .filter((group) => !group.valid)
    .map(
      (group) =>
        getSplittedTitle(group.title)
          .pop()
          ?.trim() ?? group.title,
    );

  const addButton = useButton({
    onClick: () => addItemToCart(),
    disabled: !selectionValid,
    disabledToast: i18n('POS.RequiredModifiersMissing', missingGroupNames),
    // Default POS placement sits on top of the modal's add button bar
    disabledToastPlacement: 'top',
  });

  useEffect(() => {
    if (selectionValid && isToCloseInstant && selectedVariations.length > 0) {
      setTimeout(() => {
        addItemToCart();
      }, 75);
    }
  }, [selectionValid, isToCloseInstant, selectedVariations.length]);

  const renderDisplayType = () => {
    return showGridView ? (
      <Grid
        modificationGroups={modificationGroups}
        upsellGroups={upsellGroups}
        selectedUpsales={selectedUpsales}
        onSelectVariation={onSelectVariation}
        onSelectUpsell={onSelectUpsale}
      />
    ) : (
      <>
        {modificationGroups.map((group) => (
          <Column key={group.id} modificationGroup={group} onSelect={onSelectVariation} />
        ))}
        {upsellGroups?.map((upsell) => (
          <Column
            key={upsell.id}
            upsell={upsell}
            selectedUpsales={selectedUpsales}
            onSelect={onSelectUpsale}
          />
        ))}
      </>
    );
  };

  return (
    <>
      <Content isGridView={showGridView}>
        <Backdrop onTouchStart={onClose} />
        {renderDisplayType()}
      </Content>
      {!isToCloseInstant && (
        <ButtonWrapper>
          {selectionValid ? (
            <AddButton {...addButton}>
              {isEditing ? i18n('General.Confirm') : i18n('POS.Add')}
            </AddButton>
          ) : (
            <OkButton {...addButton}>
              {isEditing ? i18n('General.Confirm') : i18n('POS.Add')}
            </OkButton>
          )}
          <CloseButton onClick={onClose} />
        </ButtonWrapper>
      )}
    </>
  );
};

export default React.memo(ItemModificationView);
